import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { map } from 'rxjs';

import { TellentNavigationItemBadge } from '@core/tellent-types';
import { BadgeComponent } from '@core/user-interface-legacy';

import { TellentNavigationLabelComponent } from '../navigation-label.component';

@Component({
  templateUrl: './navigation-label-badges.component.html',
  styleUrl: './navigation-label-badges.component.less',
  imports: [BadgeComponent],
  selector: `tlc-navigation-label-badges`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.minimized]': 'minimized()',
    '[style.visibility]': 'cssHidden()',
    '[style.transform]': 'cssTransform()',
  },
})
export class NavigationLabelBadgesComponent {
  public minimized = input<boolean>(false);
  public badges = input<TellentNavigationItemBadge[]>([]);
  public parent = inject(TellentNavigationLabelComponent);
  public scrollable = inject(CdkScrollable);
  public elRef = inject(ElementRef);

  public scrollTop = toSignal(
    this.scrollable.elementScrolled().pipe(
      map(evt => {
        const target = <HTMLElement>evt.target;
        return target.scrollTop;
      }),
    ),
    { initialValue: 0 },
  );

  public alignment = computed(() => {
    if (this.minimized()) {
      const scroll = this.scrollTop();
      const scrollBbox = this.scrollable.getElementRef().nativeElement.getBoundingClientRect();
      const parentBbox = this.elRef.nativeElement.parentElement.getBoundingClientRect();

      const topClip = parentBbox.top - scrollBbox.top + parentBbox.height / 2;
      const bottomClip = parentBbox.bottom - scrollBbox.bottom - parentBbox.height / 2;

      const offsetY = `-${scroll}px`;

      return {
        transform: `translateY(${offsetY})`,
        hidden: topClip < 0 || bottomClip > 0 ? 'hidden' : null,
      };
    }

    return {
      transform: null,
      hidden: null,
    };
  });

  public cssTransform = computed(() => this.alignment().transform);
  public cssHidden = computed(() => this.alignment().hidden);

  public safeBadges = computed(() => {
    const badges = this.badges();
    const minimized = this.minimized();

    const _badges = minimized ? badges.slice(0, 1) : badges;

    return _badges.map(badge => {
      const maybeNumber = +badge.text;
      const isNumberBadge = Number.isInteger(maybeNumber);

      if (isNumberBadge) {
        if (minimized) {
          return {
            ...badge,
            text: maybeNumber > 9 ? '9+' : `${maybeNumber}`,
          };
        }

        return {
          ...badge,
          text: maybeNumber > 99 ? '99+' : `${maybeNumber}`,
        };
      }

      return badge;
    });
  });
}
